import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, ListGroup } from 'react-bootstrap'

import moment from 'moment'

import NotificationMessageTray from '../components/common/NotificationMessageTray'
import FAIcon from '../components/FAIcon'
import { NotificationService } from '../services/NotificationService'

const PAGE_SIZE = 20

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState('all') // 'all' | 'unread'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showTray, setShowTray] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    document.title = 'GuideSmart - Notifications'
    return () => {
      document.title = 'GuideSmart'
    }
  }, [])

  const safeFromNow = dateStr => {
    try {
      if (!dateStr) return ''
      const m = moment.utc(dateStr)
      return m.isValid() ? m.local().fromNow() : ''
    } catch {
      return ''
    }
  }

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const unreadOnly = filter === 'unread'
      const result = await NotificationService.getNotifications(page, PAGE_SIZE, unreadOnly)
      setNotifications(result.results || [])
      setTotal(result.total || 0)
    } catch (err) {
      setError('Failed to load notifications.')
    } finally {
      setLoading(false)
    }
  }, [page, filter])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const handleFilterChange = newFilter => {
    setFilter(newFilter)
    setPage(1)
  }

  const handleMarkAsRead = async (id, e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    // In Unread mode: remove the item from the list so there's clear visual feedback.
    // In All mode: keep it in the list but flip its read indicator.
    if (filter === 'unread') {
      setNotifications(prev => prev.filter(n => n.id !== id))
      setTotal(prev => Math.max(0, prev - 1))
    } else {
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)))
    }
    try {
      await NotificationService.markAsRead(id)
    } catch {
      // revert on failure
      fetchNotifications()
    }
  }

  const handleMarkAllRead = async () => {
    const prev = notifications
    setNotifications(p => p.map(n => ({ ...n, isRead: true })))
    try {
      await NotificationService.markAllAsRead()
    } catch {
      setNotifications(prev)
    }
  }

  const handleDelete = async (id, e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setNotifications(prev => prev.filter(n => n.id !== id))
    setTotal(prev => Math.max(0, prev - 1))
    try {
      await NotificationService.delete(id)
    } catch {
      fetchNotifications()
    }
  }

  const handleNotificationClick = n => {
    if (!n.isRead) handleMarkAsRead(n.id)
    setSelectedMessage(n)
    setShowTray(true)
  }

  const unreadCount = notifications.filter(n => !n.isRead).length

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="mb-3">
        <h4>Notifications</h4>
      </div>

      {/* Tabs + Mark All as Read */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <ul className="nav nav-tabs mb-0 border-0">
          <li className="nav-item">
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <span
              className={`nav-link ${filter === 'all' ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleFilterChange('all')}
            >
              All
            </span>
          </li>
          <li className="nav-item">
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <span
              className={`nav-link ${filter === 'unread' ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleFilterChange('unread')}
            >
              Unread
              {filter === 'all' && unreadCount > 0 && (
                <Badge pill variant="danger" className="ml-1" style={{ fontSize: '0.65rem' }}>
                  {unreadCount}
                </Badge>
              )}
            </span>
          </li>
        </ul>
        <Button variant="outline-primary" size="sm" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
          Mark All as Read
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : error ? (
        <div className="text-danger py-3">{error}</div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <FAIcon name="bell-slash" type="far fa-2x mb-2" />
          <p className="mt-2">{filter === 'unread' ? 'No unread notifications.' : 'No notifications yet.'}</p>
        </div>
      ) : (
        <ListGroup variant="flush" className="border rounded">
          {notifications.map(n => (
            <ListGroup.Item
              key={n.id}
              action
              className={`d-flex align-items-start justify-content-between ${
                !n.isRead ? 'bg-light font-weight-bold' : ''
              }`}
              onClick={() => handleNotificationClick(n)}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="d-flex justify-content-between">
                  <span className="text-truncate mr-2" title={n.title}>
                    {n.title}
                  </span>
                  <small className="text-muted text-nowrap">{safeFromNow(n.createdUtc)}</small>
                </div>
                {n.senderName && <div className="small text-muted text-truncate">From: {n.senderName}</div>}
                <div className="small text-muted text-truncate" title={n.body}>
                  {n.body}
                </div>
                {!n.isRead && (
                  <div className="mt-1">
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                    <small className="text-primary" onClick={e => handleMarkAsRead(n.id, e)}>
                      Mark read
                    </small>
                  </div>
                )}
              </div>
              {/* Delete button */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <span
                title="Delete notification"
                className="ml-3 text-muted"
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onClick={e => handleDelete(n.id, e)}
              >
                <FAIcon name="trash-alt" type="far" />
              </span>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-center mt-3 gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Previous
          </Button>
          <span className="mx-3 text-muted small">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </Button>
        </div>
      )}

      <NotificationMessageTray isOpen={showTray} onClose={() => setShowTray(false)} message={selectedMessage} />
    </div>
  )
}

export default NotificationsPage
